// src/App.tsx
import React, { useEffect, useState, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import { formSchema, FormData } from './schema';
import { STEP_TITLES } from './questions';
import { SearchableSelect } from './SearchableSelect';
import { FeeEstimator } from './FeeEstimator';
import { DocumentUpload } from './DocumentUpload';
import { PassportScanner } from './PassportScanner';
import { ShareDraftModal } from './ShareDraftModal';
import { VisaPurposeSelector } from './VisaPurposeSelector';
import { RequirementsOverview } from './RequirementsOverview';
import { SaveResumeModal } from './SaveResumeModal';
import { saveSubmittedApplication } from './data/applicationsStore';
import { loadFormProgressAsync, SavedFormState } from './data/resumeStore';
import { safeGetItem, safeSetItem, safeRemoveItem } from './data/storage';
import { loadAdminConfig, loadAdminConfigAsync, AdminStoreConfig, getQuestionsForCountry } from './data/adminStore';

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || '';
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || '';
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || '';
const YOUR_EMAIL = process.env.REACT_APP_YOUR_EMAIL || 'myskydestinations@gmail.com';

interface ApplicantMeta {
  id: string;
  name: string;
}

const UPPERCASE_FIELD_IDS = new Set([
  'surname',
  'givenName',
  'previousSurname',
  'previousGivenName',
  'townCityBirth',
  'citizenshipId',
  'visibleMarks',
  'qualificationCollege',
  'passportNumber',
  'passportPlaceIssue',
  'passportCountryIssue',
  'fatherName',
  'fatherPlaceOfBirth',
  'motherName',
  'motherPlaceOfBirth',
  'spouseName',
  'spousePlaceOfBirth',
  'pakistanDetails',
  'presentOccupation',
  'employerName',
  'designation',
  'employerAddress',
  'pastOccupation',
  'militaryOrganization',
  'militaryDesignation',
  'militaryRank',
  'militaryPosting',
  'placesToVisit',
  'tourOperatorName',
  'tourOperatorAddress',
  'hotelName',
  'hotelPlaceCity',
  'prevIndiaAddressStayed',
  'prevIndiaCitiesVisited',
  'prevIndianVisaNo',
  'prevVisaType',
  'prevVisaPlaceIssue',
  'countriesVisited',
  'saarcCountriesVisited',
  'saarcYearVisited',
  'saarcNoVisits',
  'hotelNameIndia',
  'hotelAddressIndia',
  'referenceNameHome',
  'referenceAddressHome',
  'fullNameCompleting',
]);

const NUMERIC_FIELD_IDS = new Set([
  'phoneNo',
  'mobileNo',
  'employerPhone',
  'postalZipCode',
  'saarcYearVisited',
  'saarcNoVisits',
  'prevIndianVisaNo',
]);

const loadApplicantList = (): ApplicantMeta[] => {
  const list = safeGetItem('visaApplicantList');
  return list ? JSON.parse(list) : [{ id: 'app_1', name: 'Applicant #1 (Primary)' }];
};

const saveApplicantList = (list: ApplicantMeta[]) => {
  safeSetItem('visaApplicantList', JSON.stringify(list));
};

const loadFormDataForApplicant = (id: string): Partial<FormData> => {
  const data = safeGetItem(`visaFormData_${id}`);
  return data ? JSON.parse(data) : {};
};

const saveFormDataForApplicant = (id: string, data: Partial<FormData>) => {
  safeSetItem(`visaFormData_${id}`, JSON.stringify(data));
};

const loadStepForApplicant = (id: string): number => {
  const step = safeGetItem(`visaFormStep_${id}`);
  return step ? parseInt(step, 10) : 1;
};

const saveStepForApplicant = (id: string, step: number) => {
  safeSetItem(`visaFormStep_${id}`, step.toString());
};

function App() {
  const navigate = useNavigate();

  const [applicants, setApplicants] = useState<ApplicantMeta[]>(loadApplicantList);
  const [activeApplicantId, setActiveApplicantId] = useState<string>(applicants[0]?.id || 'app_1');
  const [currentStep, setCurrentStep] = useState<number>(() => loadStepForApplicant(activeApplicantId));
  const [isSending, setIsSending] = useState(false);
  const [isAgentUnlocked, setIsAgentUnlocked] = useState<boolean>(() => safeGetItem('visaAgentUnlocked') === 'true');
  const [showShareModal, setShowShareModal] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [isAppStarted, setIsAppStarted] = useState(false);
  const [photoBase64, setPhotoBase64] = useState<string>('');
  const [passportBase64, setPassportBase64] = useState<string>('');
  const clickTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleStatusSync = () => {
      setIsAgentUnlocked(safeGetItem('visaAgentUnlocked') === 'true');
    };
    window.addEventListener('agentStatusChanged', handleStatusSync);
    return () => window.removeEventListener('agentStatusChanged', handleStatusSync);
  }, []);

  const handleProgressBarClick = () => {
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);

    const nextCount = clickCount + 1;
    setClickCount(nextCount);

    if (nextCount >= 6) {
      window.dispatchEvent(new Event('openAgentAuthModal'));
      setClickCount(0);
    } else {
      clickTimerRef.current = setTimeout(() => {
        setClickCount(0);
      }, 3000);
    }
  };

  const handleLockAgentMode = () => {
    localStorage.setItem('visaAgentUnlocked', 'false');
    setIsAgentUnlocked(false);
    window.dispatchEvent(new Event('agentStatusChanged'));
  };

  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    trigger,
    reset,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: loadFormDataForApplicant(activeApplicantId),
    mode: 'onChange',
  });

  const formValues = watch();

  useEffect(() => {
    saveApplicantList(applicants);
  }, [applicants]);

  const handleSwitchApplicant = (targetId: string) => {
    saveFormDataForApplicant(activeApplicantId, formValues);
    setActiveApplicantId(targetId);
    reset(loadFormDataForApplicant(targetId));
    setCurrentStep(loadStepForApplicant(targetId));
  };

  const handleAddApplicant = () => {
    const newId = `app_${Date.now()}`;
    const newApplicant = { id: newId, name: `Applicant #${applicants.length + 1}` };
    const updated = [...applicants, newApplicant];
    setApplicants(updated);
    handleSwitchApplicant(newId);
  };

  const handleRemoveApplicant = (idToRemove: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (applicants.length <= 1) {
      if (window.confirm('Clear all form fields for this applicant?')) {
        handleClearCurrentForm();
      }
      return;
    }

    if (window.confirm('Are you sure you want to remove this applicant?')) {
      safeRemoveItem(`visaFormData_${idToRemove}`);
      safeRemoveItem(`visaFormStep_${idToRemove}`);

      const updated = applicants.filter((a) => a.id !== idToRemove);
      setApplicants(updated);

      if (activeApplicantId === idToRemove) {
        const nextId = updated[0].id;
        setActiveApplicantId(nextId);
        reset(loadFormDataForApplicant(nextId));
        setCurrentStep(loadStepForApplicant(nextId));
      }
    }
  };

  const handleClearCurrentForm = () => {
    if (window.confirm('Are you sure you want to clear and reset this application?')) {
      safeRemoveItem(`visaFormData_${activeApplicantId}`);
      safeRemoveItem(`visaFormStep_${activeApplicantId}`);
      reset({});
      setCurrentStep(1);
    }
  };

  const handleCopyPrimaryDetails = () => {
    const primaryData = loadFormDataForApplicant(applicants[0].id);
    reset((prev) => ({
      ...prev,
      houseNoStreet: primaryData.houseNoStreet || prev.houseNoStreet,
      villageTownCity: primaryData.villageTownCity || prev.villageTownCity,
      addressCountry: primaryData.addressCountry || prev.addressCountry,
      stateProvinceDistrict: primaryData.stateProvinceDistrict || prev.stateProvinceDistrict,
      postalZipCode: primaryData.postalZipCode || prev.postalZipCode,
      bookedHotel: primaryData.bookedHotel || prev.bookedHotel,
      hotelName: primaryData.hotelName || prev.hotelName,
      hotelPlaceCity: primaryData.hotelPlaceCity || prev.hotelPlaceCity,
    }));
    alert('📋 Copied Address & Hotel details from Primary Applicant!');
  };

  const handleAutoFillFromMRZ = (data: any) => {
    if (data.surname) setValue('surname', data.surname);
    if (data.givenName) setValue('givenName', data.givenName);
    if (data.passportNumber) setValue('passportNumber', data.passportNumber);
    if (data.dobApplicant) setValue('dobApplicant', data.dobApplicant);
    if (data.passportDateExpiry) setValue('passportDateExpiry', data.passportDateExpiry);
    if (data.gender) setValue('gender', data.gender as any);
  };

  useEffect(() => {
    const subscription = watch((value) => {
      saveFormDataForApplicant(activeApplicantId, value);
      if (value.givenName) {
        setApplicants((prev) =>
          prev.map((app) =>
            app.id === activeApplicantId ? { ...app, name: `${value.givenName} ${value.surname || ''}` } : app
          )
        );
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, activeApplicantId]);

  const handleRestoreFormState = (saved: SavedFormState) => {
    if (saved.formValues) {
      reset(saved.formValues);
    }
    if (saved.step) {
      setCurrentStep(saved.step);
      setIsAppStarted(true);
    }
    if (saved.applicants && saved.applicants.length) {
      setApplicants(saved.applicants);
    }
    if (saved.photoBase64) {
      setPhotoBase64(saved.photoBase64);
    }
    if (saved.passportBase64) {
      setPassportBase64(saved.passportBase64);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const resumeCode = params.get('resume');
    if (resumeCode) {
      loadFormProgressAsync(resumeCode).then((saved) => {
        if (saved) {
          handleRestoreFormState(saved);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [adminConfig, setAdminConfig] = useState<AdminStoreConfig>(loadAdminConfig);

  useEffect(() => {
    saveStepForApplicant(activeApplicantId, currentStep);
  }, [currentStep, activeApplicantId]);

  useEffect(() => {
    loadAdminConfigAsync().then((conf) => setAdminConfig(conf));
    const handleSync = () => setAdminConfig(loadAdminConfig());
    window.addEventListener('adminConfigUpdated', handleSync);
    return () => window.removeEventListener('adminConfigUpdated', handleSync);
  }, []);

  const dynamicQuestions = getQuestionsForCountry(adminConfig, 'INDIA');

  const currentStepQuestions = dynamicQuestions.filter((q) => {
    if (q.step !== currentStep) return false;

    // Conditional visibility for e-Business fields
    if (['indianCompanyName', 'indianCompanyAddress', 'indianCompanyWebsite'].includes(q.id)) {
      return typeof formValues.visitingIndiaFor === 'string' && formValues.visitingIndiaFor.includes('e-Business');
    }

    // Conditional visibility for e-Medical fields
    if (['indianHospitalName', 'indianHospitalAddress'].includes(q.id)) {
      return (
        typeof formValues.visitingIndiaFor === 'string' &&
        (formValues.visitingIndiaFor.includes('e-Medical') || formValues.visitingIndiaFor.includes('e-Ayush'))
      );
    }

    if (!q.dependsOn) return true;

    const fieldValue = formValues[q.dependsOn.field as keyof FormData];
    const expectedValue = q.dependsOn.value;

    if (Array.isArray(expectedValue)) {
      if (!Array.isArray(fieldValue)) return false;
      return expectedValue.every((val) => fieldValue.includes(val));
    }
    return fieldValue === expectedValue;
  });

  const handleNextStep = async () => {
    const currentStepFieldKeys = currentStepQuestions
      .filter((q) => !q.isHeading && !q.isSubHeading)
      .map((q) => q.id as keyof FormData);

    const isValid = await trigger(currentStepFieldKeys);
    if (isValid) {
      if (currentStep < 6) {
        setCurrentStep((prev) => prev + 1);
      }
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const onSubmit = async (data: FormData) => {
    setIsSending(true);
    const refId = `MYS-IND-${Math.floor(10000 + Math.random() * 90000)}`;

    let emailSentSuccess = false;

    if (EMAILJS_SERVICE_ID && EMAILJS_TEMPLATE_ID && EMAILJS_PUBLIC_KEY) {
      try {
        const formattedData = Object.entries(data)
          .filter(([_, value]) => value !== undefined && value !== null && value !== '')
          .map(([key, value]) => {
            const question = dynamicQuestions.find((q) => q.id === key);
            const label = question ? question.label : key;
            return `${label}: ${Array.isArray(value) ? value.join(', ') : value}`;
          })
          .join('\n\n');

        const templateParams = {
          to_email: YOUR_EMAIL,
          from_name: data.fullNameCompleting || data.givenName || 'Applicant',
          reply_to: data.emailAddress || YOUR_EMAIL,
          message: formattedData,
        };

        await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY);
        emailSentSuccess = true;
      } catch (error) {
        console.warn('EmailJS sending warning/error:', error);
      }
    } else {
      console.info('EmailJS environment variables not set. Application saved locally to store.');
    }

    // Always save application to store
    saveSubmittedApplication({
      id: refId,
      destination: 'INDIA',
      countryName: 'India',
      applicantName: `${data.givenName || ''} ${data.surname || ''}`.trim() || 'Applicant',
      passportNumber: data.passportNumber || 'N/A',
      nationality: data.nationality || 'Sri Lanka',
      visaCategory: data.visitingIndiaFor || 'e-Tourist Visa',
      status: 'Pending 🟡',
      submittedAt: new Date().toLocaleString(),
      totalFeeLkr: 13600,
      data,
      photoBase64,
      passportBase64,
    });

    if (emailSentSuccess) {
      alert(`✅ Application Submitted Successfully!\n\nReference: ${refId}\nEmail notification sent to ${YOUR_EMAIL}.`);
    } else {
      alert(`✅ Application Submitted Successfully!\n\nReference: ${refId}.`);
    }

    setIsSending(false);
    navigate('/review');
  };

  const progressPercent = Math.round(((currentStep - 1) / (STEP_TITLES.length - 1)) * 100);

  return (
    <div style={{ maxWidth: '940px', margin: '0 auto', padding: '1.5rem 1rem 5rem' }}>
      {/* ===== HEADER & BRANDING ===== */}
      <header
        style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #2563eb 100%)',
          borderRadius: '16px',
          padding: '2rem 1.75rem',
          color: '#ffffff',
          boxShadow: '0 10px 25px -5px rgba(30, 58, 138, 0.3)',
          marginBottom: '1.5rem',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <span
                style={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(8px)',
                  padding: '0.35rem 0.85rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}
              >
                mySky e-Visa Helper
              </span>

              {isAgentUnlocked && (
                <button
                  type="button"
                  onClick={handleLockAgentMode}
                  style={{
                    background: 'rgba(34, 197, 94, 0.25)',
                    border: '1px solid #4ade80',
                    color: '#4ade80',
                    padding: '0.35rem 0.85rem',
                    borderRadius: '9999px',
                    fontSize: '0.75rem',
                    fontWeight: '700',
                    cursor: 'pointer',
                  }}
                >
                  🟢 AGENT MODE UNLOCKED (Click to Lock)
                </button>
              )}
            </div>

            <h1 style={{ fontSize: '1.85rem', fontWeight: '800', marginTop: '0.5rem', margin: 0 }}>
              India e-Visa Application Form
            </h1>
            <p style={{ color: '#93c5fd', fontSize: '0.9rem', marginTop: '0.25rem', opacity: 0.9 }}>
              Exclusively designed for <strong>mySky Travels</strong> by{' '}
              <a
                href="https://www.madushka.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#ffffff', fontWeight: '700', textDecoration: 'underline' }}
              >
                Madushka Athapaththu
              </a>
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => navigate('/')}
              style={{
                background: 'linear-gradient(135deg, #fbbf24 0%, #d97706 100%)',
                color: '#0f172a',
                border: '1px solid #fef08a',
                padding: '0.55rem 1rem',
                borderRadius: '10px',
                fontSize: '0.85rem',
                fontWeight: '800',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                boxShadow: '0 4px 14px rgba(217, 119, 6, 0.45)',
                transition: 'all 0.15s ease',
              }}
              title="Return to mySky Destinations Hub"
            >
              🏠 Destinations Hub
            </button>

            <button
              type="button"
              onClick={() => setShowShareModal(true)}
              style={{
                background: 'rgba(59, 130, 246, 0.25)',
                border: '1px solid rgba(59, 130, 246, 0.5)',
                color: '#93c5fd',
                padding: '0.55rem 0.9rem',
                borderRadius: '10px',
                fontSize: '0.825rem',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                transition: 'all 0.15s ease',
              }}
            >
              📲 Share Draft
            </button>

            <button
              type="button"
              onClick={handleClearCurrentForm}
              style={{
                background: 'rgba(239, 68, 68, 0.2)',
                border: '1px solid rgba(239, 68, 68, 0.4)',
                color: '#fca5a5',
                padding: '0.55rem 0.9rem',
                borderRadius: '10px',
                fontSize: '0.825rem',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                transition: 'all 0.15s ease',
              }}
            >
              🗑️ Clear Form
            </button>

            <div
              onClick={handleProgressBarClick}
              style={{
                background: 'rgba(255, 255, 255, 0.12)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                borderRadius: '10px',
                padding: '0.45rem 0.85rem',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                cursor: 'pointer',
                userSelect: 'none',
              }}
              title="Form Completion Progress"
            >
              <span style={{ fontSize: '0.725rem', color: '#cbd5e1', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Progress</span>
              <span style={{ fontSize: '0.95rem', fontWeight: '900', color: '#38bdf8', background: 'rgba(56, 189, 248, 0.2)', padding: '0.15rem 0.55rem', borderRadius: '6px', border: '1px solid rgba(56, 189, 248, 0.4)' }}>
                {progressPercent}%
              </span>
            </div>
          </div>
        </div>

        {/* Progress Bar with Secret 6-Click Trigger */}
        <div
          onClick={handleProgressBarClick}
          style={{
            width: '100%',
            height: '8px',
            background: 'rgba(255, 255, 255, 0.15)',
            borderRadius: '9999px',
            marginTop: '1.5rem',
            overflow: 'hidden',
            cursor: 'pointer',
          }}
        >
          <div
            style={{
              width: `${progressPercent}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #38bdf8 0%, #34d399 100%)',
              transition: 'width 0.4s ease-in-out',
              borderRadius: '9999px',
            }}
          />
        </div>
      </header>

      {/* ===== MULTI-APPLICANT MANAGER TABS ===== */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          overflowX: 'auto',
          paddingBottom: '0.5rem',
          marginBottom: '1.5rem',
        }}
      >
        {applicants.map((app) => {
          const isActive = app.id === activeApplicantId;
          return (
            <div
              key={app.id}
              onClick={() => handleSwitchApplicant(app.id)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.6rem 1rem',
                borderRadius: '8px',
                border: isActive ? '2px solid #2563eb' : '1px solid #cbd5e1',
                background: isActive ? '#eff6ff' : '#ffffff',
                color: isActive ? '#1e3a8a' : '#475569',
                fontWeight: isActive ? '700' : '500',
                fontSize: '0.875rem',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.15s ease',
              }}
            >
              <span>👤 {app.name}</span>
              <span
                onClick={(e) => handleRemoveApplicant(app.id, e)}
                title="Remove Applicant"
                style={{
                  color: '#94a3b8',
                  fontSize: '0.8rem',
                  padding: '0.1rem 0.3rem',
                  borderRadius: '4px',
                  fontWeight: '700',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#ef4444')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#94a3b8')}
              >
                ✕
              </span>
            </div>
          );
        })}

        <button
          type="button"
          onClick={handleAddApplicant}
          style={{
            padding: '0.6rem 1rem',
            borderRadius: '8px',
            border: '1px dashed #2563eb',
            background: '#ffffff',
            color: '#2563eb',
            fontWeight: '600',
            fontSize: '0.85rem',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          ➕ Add Family Member
        </button>

        <button
          type="button"
          onClick={() => setShowSaveModal(true)}
          style={{
            padding: '0.6rem 1rem',
            borderRadius: '8px',
            border: '1px solid #10b981',
            background: '#ecfdf5',
            color: '#047857',
            fontWeight: '700',
            fontSize: '0.85rem',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          💾 Save Progress Code
        </button>

        <button
          type="button"
          onClick={() => setShowResumeModal(true)}
          style={{
            padding: '0.6rem 1rem',
            borderRadius: '8px',
            border: '1px solid #6366f1',
            background: '#eef2ff',
            color: '#4338ca',
            fontWeight: '700',
            fontSize: '0.85rem',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          🔑 Enter Recovery Code
        </button>

        {activeApplicantId !== applicants[0]?.id && (
          <button
            type="button"
            onClick={handleCopyPrimaryDetails}
            style={{
              padding: '0.6rem 1rem',
              borderRadius: '8px',
              border: '1px solid #10b981',
              background: '#f0fdf4',
              color: '#047857',
              fontWeight: '600',
              fontSize: '0.85rem',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              marginLeft: 'auto',
            }}
          >
            📋 Copy Primary Details
          </button>
        )}
      </div>

      {/* ===== STEPPER NAVIGATION HEADER ===== */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
          gap: '0.5rem',
          marginBottom: '2rem',
        }}
      >
        {STEP_TITLES.map((st) => {
          const isActive = st.step === currentStep;
          const isCompleted = st.step < currentStep;

          return (
            <button
              key={st.step}
              type="button"
              onClick={() => {
                if (isCompleted) setCurrentStep(st.step);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 0.65rem',
                borderRadius: '10px',
                border: isActive
                  ? '2px solid #2563eb'
                  : isCompleted
                  ? '1px solid #10b981'
                  : '1px solid #e2e8f0',
                background: isActive ? '#eff6ff' : isCompleted ? '#f0fdf4' : '#ffffff',
                color: isActive ? '#1e3a8a' : isCompleted ? '#047857' : '#64748b',
                cursor: isCompleted ? 'pointer' : 'default',
                fontWeight: isActive ? '700' : '500',
                fontSize: '0.825rem',
                textAlign: 'left',
                transition: 'all 0.2s ease',
              }}
            >
              <span style={{ fontSize: '1.1rem' }}>{isCompleted ? '✅' : st.icon}</span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {st.title}
              </span>
            </button>
          );
        })}
      </div>

      {/* ===== STEP FORM CONTAINER ===== */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="card-container animate-fade-in">
          {currentStep === 1 && !isAppStarted ? (
            <RequirementsOverview
              countryCode="INDIA"
              onStartApplication={() => setIsAppStarted(true)}
            />
          ) : (
            <>
              {(currentStep === 2 || currentStep === 3) && (
                <PassportScanner onAutoFill={handleAutoFillFromMRZ} />
              )}

          {currentStepQuestions.map((q) => {
            if (q.type === 'heading') {
              return (
                <div key={q.id} style={{ marginBottom: '1.5rem', borderBottom: '2px solid #1e3a8a', paddingBottom: '0.5rem' }}>
                  <h2 style={{ fontSize: '1.4rem', color: '#0f172a', fontWeight: '800' }}>
                    {q.label}
                  </h2>
                </div>
              );
            }

            if (q.type === 'subheading') {
              return (
                <div key={q.id} style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>
                  <h3 style={{ fontSize: '1.1rem', color: '#2563eb', fontWeight: '700' }}>
                    {q.label}
                  </h3>
                </div>
              );
            }

            const shouldUppercase = UPPERCASE_FIELD_IDS.has(q.id);
            const isNumeric = NUMERIC_FIELD_IDS.has(q.id);

            return (
              <div key={q.id} style={{ marginBottom: '1.5rem' }}>
                <label
                  htmlFor={q.id}
                  style={{
                    fontWeight: '600',
                    display: 'block',
                    marginBottom: '0.35rem',
                    color: '#1e293b',
                    fontSize: '0.925rem',
                  }}
                >
                  {q.label.startsWith('*') ? (
                    <>
                      <span style={{ color: '#ef4444', marginRight: '3px' }}>*</span>
                      {q.label.substring(1)}
                    </>
                  ) : (
                    q.label
                  )}
                </label>

                <Controller
                  name={q.id as any}
                  control={control}
                  render={({ field }) => {
                    const valueAsString = (field.value as string) ?? '';

                    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                      let rawVal = e.target.value;
                      if (shouldUppercase) {
                        rawVal = rawVal.toUpperCase();
                      }
                      field.onChange(rawVal);
                    };

                    switch (q.type) {
                      case 'select':
                        if (q.id === 'visitingIndiaFor') {
                          return (
                            <VisaPurposeSelector
                              value={valueAsString}
                              onChange={field.onChange}
                              error={!!errors[q.id as keyof FormData]}
                            />
                          );
                        }
                        return (
                          <SearchableSelect
                            id={q.id}
                            options={q.options || []}
                            value={valueAsString}
                            onChange={field.onChange}
                            placeholder="Type or click to select..."
                            error={!!errors[q.id as keyof FormData]}
                          />
                        );

                      case 'radio':
                        return (
                          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.35rem' }}>
                            {q.options?.map((opt) => {
                              const isChecked = field.value === opt;
                              return (
                                <label
                                  key={opt}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.75rem 1.25rem',
                                    minHeight: '48px',
                                    borderRadius: '8px',
                                    border: isChecked ? '2px solid #2563eb' : '1px solid #cbd5e1',
                                    background: isChecked ? '#eff6ff' : '#ffffff',
                                    color: isChecked ? '#1e3a8a' : '#334155',
                                    fontWeight: isChecked ? '700' : '500',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease',
                                  }}
                                >
                                  <input
                                    type="radio"
                                    value={opt}
                                    checked={isChecked}
                                    onChange={() => field.onChange(opt)}
                                    name={field.name}
                                    ref={field.ref}
                                    style={{ accentColor: '#2563eb', width: '18px', height: '18px' }}
                                  />
                                  {opt}
                                </label>
                              );
                            })}
                          </div>
                        );

                      case 'checkbox':
                        const currentValues = Array.isArray(field.value) ? field.value : [];
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.35rem' }}>
                            {q.options?.map((opt) => {
                              const isChecked = currentValues.includes(opt);
                              return (
                                <label
                                  key={opt}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.75rem 1rem',
                                    minHeight: '48px',
                                    borderRadius: '8px',
                                    border: isChecked ? '2px solid #10b981' : '1px solid #cbd5e1',
                                    background: isChecked ? '#ecfdf5' : '#ffffff',
                                    color: isChecked ? '#047857' : '#334155',
                                    fontWeight: isChecked ? '600' : '400',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease',
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    value={opt}
                                    checked={isChecked}
                                    onChange={() => {
                                      if (isChecked) {
                                        field.onChange(currentValues.filter((v: string) => v !== opt));
                                      } else {
                                        field.onChange([...currentValues, opt]);
                                      }
                                    }}
                                    name={field.name}
                                    ref={field.ref}
                                    style={{ accentColor: '#10b981', width: '18px', height: '18px' }}
                                  />
                                  {opt}
                                </label>
                              );
                            })}
                          </div>
                        );

                      case 'textarea':
                        return (
                          <textarea
                            id={q.id}
                            rows={3}
                            placeholder={q.placeholder}
                            value={valueAsString}
                            onChange={handleTextChange}
                            onBlur={field.onBlur}
                            name={field.name}
                            ref={field.ref}
                            autoCapitalize={shouldUppercase ? 'characters' : 'sentences'}
                          />
                        );

                      case 'date':
                        return (
                          <input
                            id={q.id}
                            type="date"
                            placeholder={q.placeholder}
                            value={valueAsString}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            name={field.name}
                            ref={field.ref}
                          />
                        );

                      default:
                        return (
                          <input
                            id={q.id}
                            type={q.type}
                            placeholder={q.placeholder}
                            value={valueAsString}
                            onChange={handleTextChange}
                            onBlur={field.onBlur}
                            name={field.name}
                            ref={field.ref}
                            autoCapitalize={shouldUppercase ? 'characters' : 'off'}
                            inputMode={isNumeric ? 'numeric' : q.type === 'email' ? 'email' : 'text'}
                          />
                        );
                    }
                  }}
                />

                {q.tooltip && (
                  <div className="field-tooltip-box">
                    <span>ℹ️</span> <span>{q.tooltip}</span>
                  </div>
                )}

                {errors[q.id as keyof FormData]?.message && (
                  <p style={{ color: '#ef4444', fontSize: '0.85rem', marginTop: '0.35rem', fontWeight: '500' }}>
                    {String(errors[q.id as keyof FormData]?.message)}
                  </p>
                )}
              </div>
            );
          })}

          <FeeEstimator nationality={formValues.nationality as string} visitingPurpose={formValues.visitingIndiaFor as string} />

          {currentStep === 3 && (
            <DocumentUpload
              onPhotoUpload={(b) => setPhotoBase64(b)}
              onPassportUpload={(b) => setPassportBase64(b)}
            />
          )}

          {/* ===== DESKTOP NAVIGATION CONTROLS ===== */}
          <div
            className="desktop-nav-controls"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '2.5rem',
              paddingTop: '1.5rem',
              borderTop: '1px solid #e2e8f0',
            }}
          >
            {currentStep > 1 ? (
              <button type="button" onClick={handlePrevStep} className="btn-secondary">
                ← Back
              </button>
            ) : (
              <div />
            )}

            {currentStep < 6 ? (
              <button type="button" onClick={handleNextStep} className="btn-primary">
                Save & Next Step →
              </button>
            ) : (
              <button type="submit" disabled={isSending} className="btn-primary" style={{ background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)' }}>
                {isSending ? 'Sending Application...' : 'Submit Application ✅'}
              </button>
            )}
          </div>

          {/* ===== MOBILE FIXED BOTTOM NAVIGATION BAR ===== */}
          <div className="mobile-fixed-bottom-bar">
            {currentStep > 1 ? (
              <button type="button" onClick={handlePrevStep} className="btn-secondary">
                ← Back
              </button>
            ) : (
              <div style={{ flex: 1 }} />
            )}

            {currentStep < 6 ? (
              <button type="button" onClick={handleNextStep} className="btn-primary">
                Save & Next Step →
              </button>
            ) : (
              <button type="submit" disabled={isSending} className="btn-primary" style={{ background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)' }}>
                {isSending ? 'Sending...' : 'Submit ✅'}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  </form>

      {showShareModal && (
        <ShareDraftModal
          applicantName={`${formValues.givenName || ''} ${formValues.surname || ''}`.trim()}
          formData={formValues}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {showSaveModal && (
        <SaveResumeModal
          mode="SAVE"
          destination="India"
          step={currentStep}
          formValues={formValues}
          applicants={applicants}
          photoBase64={photoBase64}
          passportBase64={passportBase64}
          onClose={() => setShowSaveModal(false)}
          onRestore={handleRestoreFormState}
        />
      )}

      {showResumeModal && (
        <SaveResumeModal
          mode="RESUME"
          destination="India"
          step={currentStep}
          onClose={() => setShowResumeModal(false)}
          onRestore={handleRestoreFormState}
        />
      )}
    </div>
  );
}

export default App;
